import {
  Briefcase,
  Camera,
  ExternalLink,
  Globe,
  Mail,
  type LucideIcon,
} from "lucide-react-native";
import React from "react";
import { StyleSheet, Text, View } from "react-native";
import FacebookSvgIcon from "./social-icons/FacebookSvgIcon";
import TikTokSvgIcon from "./social-icons/TikTokSvgIcon";
import WhatsAppSvgIcon from "./social-icons/WhatsAppSvgIcon";
import YouTubeSvgIcon from "./social-icons/YouTubeSvgIcon";

type SocialPlatform =
  | "email"
  | "facebook"
  | "instagram"
  | "linkedin"
  | "tiktok"
  | "twitter"
  | "whatsapp"
  | "website"
  | "youtube"
  | "unknown";

type Props = {
  platform?: string | null;
  url?: string | null;
  size?: number;
  color?: string;
};

function normalize(value?: string | null) {
  return String(value ?? "").trim().toLowerCase();
}

function detectFromText(value: string): SocialPlatform | null {
  const text = normalize(value);
  if (!text) return null;

  if (text.includes("mailto:") || text === "email" || text === "mail") {
    return "email";
  }

  if (text.includes("facebook") || text.includes("fb.com") || text === "fb") {
    return "facebook";
  }

  if (text.includes("instagram") || text === "ig") return "instagram";
  if (text.includes("linkedin") || text === "in") return "linkedin";
  if (text.includes("tiktok") || text.includes("tik tok")) return "tiktok";
  if (text.includes("youtube") || text.includes("youtu.be")) return "youtube";
  if (
    text.includes("whatsapp") ||
    text.includes("wa.me") ||
    text.includes("api.whatsapp.com")
  ) {
    return "whatsapp";
  }

  if (text === "x" || text.includes("twitter") || text.includes("x.com")) {
    return "twitter";
  }

  if (text === "website" || text === "web") return "website";

  return null;
}

export function detectSocialPlatform(
  platform?: string | null,
  url?: string | null
): SocialPlatform {
  return detectFromText(platform ?? "") ?? detectFromText(url ?? "") ?? "unknown";
}

function getIcon(platform: SocialPlatform): LucideIcon {
  if (platform === "email") return Mail;
  if (platform === "instagram") return Camera;
  if (platform === "linkedin") return Briefcase;
  if (platform === "website") return Globe;

  return ExternalLink;
}

function getBadge(platform: SocialPlatform) {
  if (platform === "linkedin") return "in";
  if (platform === "twitter") return "X";
  return "";
}

export default function SocialIcon({
  platform,
  url,
  size = 16,
  color = "#111827",
}: Props) {
  const detectedPlatform = detectSocialPlatform(platform, url);
  const badge = getBadge(detectedPlatform);

  if (badge) {
    return (
      <View pointerEvents="none" style={[styles.wrap, { width: size + 4 }]}>
        <Text
          style={[
            styles.badge,
            {
              color,
              fontSize: Math.max(10, Math.round(size * 0.72)),
              lineHeight: Math.max(12, Math.round(size * 0.9)),
            },
          ]}
          numberOfLines={1}
        >
          {badge}
        </Text>
      </View>
    );
  }

  if (detectedPlatform === "facebook") {
    return (
      <View pointerEvents="none" style={styles.wrap}>
        <FacebookSvgIcon size={size} color={color} />
      </View>
    );
  }

  if (detectedPlatform === "tiktok") {
    return (
      <View pointerEvents="none" style={styles.wrap}>
        <TikTokSvgIcon size={size} color={color} />
      </View>
    );
  }

  if (detectedPlatform === "youtube") {
    return (
      <View pointerEvents="none" style={styles.wrap}>
        <YouTubeSvgIcon size={size} color={color} />
      </View>
    );
  }

  if (detectedPlatform === "whatsapp") {
    return (
      <View pointerEvents="none" style={styles.wrap}>
        <WhatsAppSvgIcon size={size} color={color} />
      </View>
    );
  }

  const Icon = getIcon(detectedPlatform);

  return (
    <View pointerEvents="none" style={styles.wrap}>
      <Icon size={size} color={color} strokeWidth={2.5} />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: "center",
    justifyContent: "center",
  },

  badge: {
    fontWeight: "900",
    textAlign: "center",
    includeFontPadding: false,
  },
});
